import type { ReactNode } from 'react';

import { REVEAL_ATTRIBUTE } from '../../../shared/hooks/useReveal';

export interface RevealProps {
  readonly children: ReactNode;
  readonly as?: 'div' | 'li' | 'section';
  readonly className?: string;
}

/**
 * Marks a subtree for scroll reveal.
 *
 * Renders VISIBLE. `useReveal` hides it only after confirming an IntersectionObserver
 * exists and the effect has run, so a failure anywhere in that chain leaves readable
 * content rather than a blank page. Getting this inversion wrong is how an animated site
 * ships as an empty one.
 */
export function Reveal({ children, as = 'div', className = '' }: RevealProps) {
  const props = {
    [REVEAL_ATTRIBUTE]: 'true',
    className: `reveal ${className}`.trim(),
  };

  if (as === 'li') {
    return <li {...props}>{children}</li>;
  }

  if (as === 'section') {
    return <section {...props}>{children}</section>;
  }

  return <div {...props}>{children}</div>;
}

/** Wraps a list so its children stagger. Read by useReveal to compute an index. */
export function RevealGroup({
  children,
  className = '',
}: {
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return (
    <div {...{ [`${REVEAL_ATTRIBUTE}-group`]: 'true' }} className={className}>
      {children}
    </div>
  );
}
